import { z } from 'zod';
import { oltCredentialInputSchema } from './onboarding.js';

/**
 * Import from SmartOLT, live via its API.
 *
 * The fleet was provisioned by SmartOLT and some of what it holds no chassis
 * can answer for — subscriber names (the C650 masks every one), zone and ODB
 * assignments, free-text notes. Those were typed into a UI, not onto an OLT.
 *
 * Two calls, never one, exactly like bulk actions:
 *
 *   1. POST an import → a PREVIEW. It reads SmartOLT, matches on serial,
 *      and freezes a field-by-field diff per ONU: what SmartOLT holds, what
 *      SimpleACS holds, and what would change. It writes no ONU.
 *   2. POST that preview's id with the count typed back → an APPLY. The write
 *      set comes from the frozen rows, never from re-reading SmartOLT.
 *
 * And the rule that outranks convenience: the merge is ADDITIVE. A field
 * SimpleACS already holds is reported as a conflict and skipped. There is no
 * request shape, flag or setting in this file that means "overwrite" — if the
 * operator ever decides one field genuinely should be overwritable, that is a
 * decision they make, not one the code makes for them.
 */

/** How long a preview is worth applying. The fleet moves; a stale diff lies. */
export const SMARTOLT_IMPORT_TTL_MS = 60 * 60 * 1000;

/** Default ceiling on how many ONUs one apply may write. */
export const SMARTOLT_APPLY_CAP_DEFAULT = 200;

/**
 * The fields this import can bring across.
 *
 * Every one of them is something set in SmartOLT's UI rather than read off a
 * chassis — which is precisely why a sweep can never recover it. Anything the
 * OLT itself reports (state, optics, ONU type, PON port) is deliberately
 * absent: the chassis is the source for those and importing them would be a
 * second, staler answer to a question already answered.
 *
 * `externalId` is the odd one out and is treated as such everywhere. The other
 * four DESCRIBE a subscriber; this one ADDRESSES them — it is what 88 of the
 * 107 endpoints in SmartOLT's own API collection are keyed on, so it is the
 * identifier a migrating customer's existing integrations already send. It is
 * the only field here that is expensive to add after the fact, because the
 * SmartOLT instance holding it will have been switched off.
 */
export const smartoltFieldSchema = z.enum(['name', 'zone', 'odb', 'notes', 'externalId']);
export type SmartoltField = z.infer<typeof smartoltFieldSchema>;

export const SMARTOLT_FIELDS: readonly SmartoltField[] = [
  'name',
  'zone',
  'odb',
  'notes',
  'externalId',
];

/**
 * The four DESCRIPTIVE fields, as one list.
 *
 * `externalId` is deliberately not in it. The other four describe a
 * subscriber, and getting one wrong mislabels an ONU where somebody notices;
 * the external id ADDRESSES a subscriber, and getting it wrong routes a third
 * party's API call to a different one, silently, with a 200. It is handled by
 * its own rules everywhere the descriptive four are handled by a loop, and
 * this constant exists so that "the loop" cannot quietly grow to include it.
 */
export const SMARTOLT_DESCRIPTIVE_FIELDS = ['name', 'zone', 'odb', 'notes'] as const;
export type SmartoltDescriptiveField = (typeof SMARTOLT_DESCRIPTIVE_FIELDS)[number];

export const SMARTOLT_FIELD_LABELS: Record<SmartoltField, string> = {
  name: 'Subscriber name',
  zone: 'Zone',
  odb: 'Splitter (ODB)',
  notes: 'Notes',
  externalId: 'External ID',
};

/**
 * Where SmartOLT lives.
 *
 * Every instance is `https://<subdomain>.smartolt.com`. That is not a guess:
 * `docs/postman_collection.json` is SmartOLT's own API collection, and all 107
 * requests in it are `https://{{subdomain}}.smartolt.com/api/…` with a single
 * `X-Token: {{api_key}}` header. There is no variation to accommodate, so the
 * connection asks for a SUBDOMAIN and derives the rest — an operator can no
 * longer get the scheme, the host, the auth style or the header name wrong,
 * because none of them is theirs to type.
 */
export const SMARTOLT_HOST_SUFFIX = 'smartolt.com';

/** The one header SmartOLT authenticates with. 107 of 107 requests use it. */
export const SMARTOLT_AUTH_HEADER = 'X-Token';

/** `mycompany` → `https://mycompany.smartolt.com`. The whole derivation. */
export function smartoltBaseUrlFor(subdomain: string): string {
  return `https://${subdomain}.${SMARTOLT_HOST_SUFFIX}`;
}

/**
 * A subdomain label, or null if that is not what this is.
 *
 * Generous about what it accepts and strict about what it returns: an operator
 * who pastes `https://mycompany.smartolt.com/`, `mycompany.smartolt.com` or
 * `MyCompany` all meant `mycompany`, and refusing the paste would be a puzzle
 * rather than a safeguard. Anything that is NOT a `smartolt.com` host — a
 * self-hosted instance, a reverse proxy, an IP — returns null, because that is
 * a full base URL and belongs in the override rather than being mangled into a
 * label that would silently point at the wrong company's tenant.
 */
export function parseSmartoltSubdomain(raw: string): string | null {
  let value = raw.trim().toLowerCase();
  if (value === '') return null;
  value = value.replace(/^[a-z][a-z0-9+.-]*:\/\//, ''); // scheme
  value = value.split('/')[0] ?? ''; // path
  value = value.split('?')[0] ?? ''; // query
  value = value.split('@').pop() ?? ''; // credentials
  value = value.split(':')[0] ?? ''; // port
  if (value.endsWith(`.${SMARTOLT_HOST_SUFFIX}`)) {
    value = value.slice(0, -`.${SMARTOLT_HOST_SUFFIX}`.length);
  }
  // What is left must be ONE label. Anything with a dot still in it is some
  // other host, and guessing which part of it is the tenant is exactly the
  // kind of cleverness that connects to a stranger's SmartOLT.
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value)) return null;
  return value;
}

/** Which of the two settings the effective base URL came from. */
export type SmartoltBaseUrlSource = 'subdomain' | 'override' | 'none';

/** The connection, as the settings screen sees it. The token is included so the
 * field survives a refresh — the same rule as the gateway and storage keys. */
export interface SmartoltSettings {
  /** The tenant, e.g. `mycompany`. Empty on a connection configured before
   * subdomains existed, or on one deliberately pointed at an override. */
  subdomain: string;
  /**
   * A full base URL, for a self-hosted or proxied instance.
   *
   * Also where a connection configured BEFORE this field existed lives: its
   * stored `smartolt.baseUrl` is read as an override, never re-read as a
   * subdomain. The two are separate settings keys, which is the whole
   * discriminator — nothing has to guess what an old value meant.
   */
  baseUrlOverride: string;
  /** The base URL actually used: derived from `subdomain` when there is one,
   * `baseUrlOverride` otherwise. Never typed directly. */
  baseUrl: string;
  baseUrlSource: SmartoltBaseUrlSource;
  token: string;
  /** Path the ONU list is read from, relative to the base URL. */
  onusPath: string;
  /** Page size asked for, when the API paginates at all. */
  pageSize: number;
  /** Ceiling on how many ONUs one apply may write. */
  applyCap: number;
  configured: boolean;
}

export const updateSmartoltSettingsSchema = z.object({
  /** `mycompany`, or a pasted `https://mycompany.smartolt.com` — both work. */
  subdomain: z.string().max(300).optional(),
  /** Self-hosted or proxied only. Used when no subdomain is set. */
  baseUrlOverride: z.string().max(300).optional(),
  /** Legacy alias for `baseUrlOverride`, kept so an older client still saves. */
  baseUrl: z.string().max(300).optional(),
  token: z.string().max(500).optional(),
  onusPath: z.string().max(200).optional(),
  pageSize: z.number().int().min(1).max(2000).optional(),
  applyCap: z.number().int().min(1).max(5000).optional(),
});
export type UpdateSmartoltSettingsInput = z.infer<typeof updateSmartoltSettingsSchema>;

/**
 * What a test connection found. Nothing is imported and nothing is stored.
 *
 * `fieldsSeen` is the useful part: it names the keys SmartOLT actually
 * returned, which is how the adapter's assumptions get corrected against the
 * real API instead of against a fixture.
 */
export interface SmartoltProbe {
  ok: boolean;
  status: number | null;
  latencyMs: number;
  /** Records the first page returned. */
  recordCount: number;
  /** A total, if the response carried one. */
  totalReported: number | null;
  /** Whether the response paginated in a shape the adapter recognises. */
  paginated: boolean;
  /** Keys present on the first record — the ground truth for the adapter. */
  fieldsSeen: string[];
  /** A few serials, so an operator can recognise their own fleet. */
  sampleSerials: string[];
  /** How many of the first page carried each importable field. */
  fieldCoverage: Partial<Record<SmartoltField, number>>;
  warnings: string[];
  error: string | null;
}

/** What happened to one field on one ONU. Only `fill` ever becomes a write. */
export const smartoltDecisionSchema = z.enum([
  /** SimpleACS holds nothing here and SmartOLT does. This is the import. */
  'fill',
  /** SimpleACS already holds a DIFFERENT value. Reported and skipped. */
  'conflict',
  /** Both sides agree. Nothing to do. */
  'same',
  /** SmartOLT's value could not be resolved here — an unknown zone or ODB. */
  'unresolved',
  /** The field was not ticked for this import, or SmartOLT holds nothing. */
  'ignored',
]);
export type SmartoltDecision = z.infer<typeof smartoltDecisionSchema>;

export interface SmartoltFieldChange {
  field: SmartoltField;
  /** What SmartOLT holds, as text. */
  remote: string | null;
  /** What SimpleACS holds, as text. */
  local: string | null;
  decision: SmartoltDecision;
  /** Why, in words an operator can act on. */
  reason: string;
}

export type SmartoltTargetKind = 'match' | 'unknown_here' | 'missing_there';

export interface SmartoltImportTarget {
  kind: SmartoltTargetKind;
  onuId: string | null;
  onuNumber: number | null;
  serial: string;
  serialKey: string;
  name: string | null;
  interfaceName: string | null;
  oltId: string | null;
  oltName: string | null;
  changes: SmartoltFieldChange[];
  /** At least one field would be filled. */
  fillable: boolean;
  outcome: 'ok' | 'skipped' | 'failed' | 'conflict' | null;
  message: string | null;
  finishedAt: number | null;
}

export type SmartoltImportStatus = 'draft' | 'applied' | 'expired' | 'failed' | 'discarded';

export interface SmartoltImportSummary {
  id: string;
  orgId: string | null;
  baseUrl: string;
  status: SmartoltImportStatus;
  fields: SmartoltField[];
  sourceCount: number;
  matchedCount: number;
  fillableCount: number;
  fieldCount: number;
  conflictCount: number;
  unknownHereCount: number;
  missingThereCount: number;
  cap: number;
  overCap: boolean;
  createdAt: number;
  createdBy: string | null;
  expiresAt: number;
  appliedAt: number | null;
  appliedBy: string | null;
  appliedCount: number;
  skippedCount: number;
  failedCount: number;
  note: string | null;
}

export interface SmartoltImport extends SmartoltImportSummary {
  targets: SmartoltImportTarget[];
}

/**
 * Ask for a preview.
 *
 * `fields` defaults to all of them; `oltId` narrows the SimpleACS side to one
 * chassis, which is how the operator's first real import stays small enough to
 * read. `serials` narrows it further to an explicit list — the shape the very
 * first import should use, because "restore the C650 test ONU's name" is one
 * serial and should be reviewable as one row.
 */
export const createSmartoltImportSchema = z.object({
  fields: z
    .array(smartoltFieldSchema)
    .min(1)
    .max(SMARTOLT_FIELDS.length)
    .default([...SMARTOLT_FIELDS]),
  oltId: z.string().min(1).max(64).nullable().default(null),
  serials: z.array(z.string().min(1).max(64)).max(500).nullable().default(null),
  /**
   * Stop reading SmartOLT after this many records.
   *
   * A bound on the READ, distinct from the cap on the write. ~4,657 ONUs is
   * more than anybody reviews in one sitting, and a preview nobody reads is
   * the failure this whole split exists to prevent.
   */
  maxRecords: z.number().int().min(1).max(20000).default(2000),
});
export type CreateSmartoltImportInput = z.infer<typeof createSmartoltImportSchema>;

export const applySmartoltImportSchema = z.object({
  /** The number of ONUs shown on the preview, typed back. */
  confirmCount: z.number().int().min(0).max(20000),
});
export type ApplySmartoltImportInput = z.infer<typeof applySmartoltImportSchema>;

// --- The migration wizard --------------------------------------------------------

/**
 * "Give the SmartOLT URL and API key, choose what to bring, and the customer
 * is migrated" (operator, 2026-09-10; docs/smartolt-api-compatibility.md,
 * "The import wizard"). A MIGRATION reads their whole inventory — not only the
 * ONU fields the older import fills — freezes one reviewable diff per group,
 * and applies the groups the operator confirmed, in dependency order. Nothing
 * read from SmartOLT is written in the same act, and a field this platform
 * already holds is never overwritten: the additive rule of the older import
 * stands for every group here.
 */
/**
 * How long a frozen migration plan may still be applied. Longer than the older
 * import's hour because a migration is applied GROUP BY GROUP and a failed
 * group is retried from the same frozen plan — a plan that expired between
 * the OLTs landing and the ONUs being retried would force a second review of
 * everything already done. (Added with the API side, 2026-09-10.)
 */
export const SMARTOLT_MIGRATION_TTL_MS = 24 * 60 * 60 * 1000;

export const SMARTOLT_MIGRATION_GROUPS = [
  'olts',
  'vlans',
  'zones',
  'odbs',
  'onu_types',
  'speed_profiles',
  'onus',
  'ports',
] as const;
export const smartoltMigrationGroupSchema = z.enum(SMARTOLT_MIGRATION_GROUPS);
export type SmartoltMigrationGroup = z.infer<typeof smartoltMigrationGroupSchema>;

/** Apply order: a group only ever depends on groups before it. */
export const SMARTOLT_MIGRATION_ORDER: readonly SmartoltMigrationGroup[] = SMARTOLT_MIGRATION_GROUPS;

export const SMARTOLT_MIGRATION_GROUP_LABELS: Record<SmartoltMigrationGroup, string> = {
  olts: 'OLTs',
  vlans: 'VLANs',
  zones: 'Zones',
  odbs: 'ODBs (splitters)',
  onu_types: 'ONU types',
  speed_profiles: 'Speed profiles',
  onus: 'ONUs',
  ports: 'Ethernet / WiFi port modes',
};

/** A connection typed into the wizard, used instead of the stored settings when given. */
export const smartoltConnectionInputSchema = z.object({
  /** `mycompany`, or a pasted `https://mycompany.smartolt.com` — both work. */
  subdomain: z.string().max(300).optional(),
  /** Self-hosted or proxied only. Used when no subdomain is given. */
  baseUrlOverride: z.string().max(300).optional(),
  token: z.string().min(1).max(500),
});
export type SmartoltConnectionInput = z.infer<typeof smartoltConnectionInputSchema>;

/** Step 1's answer: what the token can see, one count per group. */
export interface SmartoltMigrationProbe {
  ok: boolean;
  /** Where it connected, for the operator to recognise. */
  baseUrl: string;
  counts: Partial<Record<SmartoltMigrationGroup, number | null>>;
  /** Groups the token could not read, each with the reason. */
  warnings: string[];
}

export const probeSmartoltMigrationSchema = z.object({
  connection: smartoltConnectionInputSchema.optional(),
});

export const createSmartoltMigrationSchema = z.object({
  connection: smartoltConnectionInputSchema.optional(),
  groups: z.array(smartoltMigrationGroupSchema).min(1),
  /** Stop reading SmartOLT's ONUs after this many records (preview safety). */
  onuMaxRecords: z.number().int().min(1).max(20_000).default(5_000),
});
export type CreateSmartoltMigrationInput = z.infer<typeof createSmartoltMigrationSchema>;

/**
 * What the review says about one thing in one group.
 *   create    — nothing here matches; a row will be created
 *   fill      — a row matches and empty fields will be filled (listed)
 *   unchanged — a row matches and holds everything already
 *   conflict  — a row matches and holds a DIFFERENT value; shown, never overwritten
 *   refused   — cannot be brought, with the reason (parent not selected, unknown type…)
 */
export const smartoltMigrationDecisionSchema = z.enum([
  'create',
  'fill',
  'unchanged',
  'conflict',
  'refused',
]);
export type SmartoltMigrationDecision = z.infer<typeof smartoltMigrationDecisionSchema>;

export interface SmartoltMigrationChange {
  field: string;
  /** What this platform holds now (null = empty). */
  from: string | null;
  /** What SmartOLT carries. */
  to: string | null;
}

export interface SmartoltMigrationItem {
  /** Stable within the group: SmartOLT's id, or the serial for an ONU. */
  key: string;
  /**
   * OLTs only. `needed`: the OLT will exist here (created by this migration,
   * or already here) with no login stored, so the review asks for SmartOLT's
   * credentials for it — their API never returns them, and without them the
   * platform cannot read the chassis at all. `held`: a login is already
   * stored and is never overwritten. Absent on every other group's items.
   */
  credentials?: 'needed' | 'held';
  /**
   * OLTs only: the chassis the credentials are for — as it will be dialled
   * here (this platform's address for a matched OLT, SmartOLT's for a new
   * one) — so the review can check a typed login before anything is stored.
   */
  olt?: { vendor: 'zte' | 'huawei'; mgmtIp: string; cliPort: number; snmpPort: number };
  /** How the review names it: an OLT name, "VLAN 10 on gachie1", a serial + name. */
  label: string;
  decision: SmartoltMigrationDecision;
  /** The reason for a conflict or a refusal; a one-line note otherwise. */
  detail: string | null;
  changes: SmartoltMigrationChange[];
}

export type SmartoltMigrationGroupStatus = 'pending' | 'applied' | 'failed' | 'skipped';

export interface SmartoltMigrationGroupPlan {
  group: SmartoltMigrationGroup;
  counts: Record<SmartoltMigrationDecision, number>;
  items: SmartoltMigrationItem[];
  status: SmartoltMigrationGroupStatus;
  appliedAt: number | null;
  /** What landed, in one sentence, or why the group failed. */
  outcome: string | null;
}

export type SmartoltMigrationStatus =
  | 'draft'
  | 'applying'
  | 'applied'
  | 'partial'
  | 'expired'
  | 'discarded';

export interface SmartoltMigrationSummary {
  id: string;
  status: SmartoltMigrationStatus;
  baseUrl: string;
  groups: SmartoltMigrationGroup[];
  createdAt: number;
  createdBy: string | null;
  expiresAt: number;
  appliedAt: number | null;
  /** Read-time warnings (a group the token could not read, a truncated ONU list). */
  warnings: string[];
}

export interface SmartoltMigration extends SmartoltMigrationSummary {
  plans: SmartoltMigrationGroupPlan[];
}

/**
 * Apply the groups the operator confirmed. `confirmCounts` is, per group, the
 * number of CREATE + FILL items the review showed — typed back and compared to
 * the frozen plan's own number, the same discipline as the older import.
 */
export const applySmartoltMigrationSchema = z.object({
  groups: z.array(smartoltMigrationGroupSchema).min(1),
  confirmCounts: z.record(smartoltMigrationGroupSchema, z.number().int().min(0)),
  /**
   * The login and communities for each OLT whose review item says
   * `credentials: 'needed'`, keyed by that item's key (SmartOLT's OLT id).
   * Stored encrypted on the OLT, never over a login already held, and followed
   * by a read-only first sweep. Optional per OLT: one left out is created
   * without, exactly as before, and gets its credentials from Edit later.
   */
  oltCredentials: z.record(z.string(), oltCredentialInputSchema).optional(),
});
export type ApplySmartoltMigrationInput = z.infer<typeof applySmartoltMigrationSchema>;
